import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useGetRepairTicketsQuery } from '../../../store/api/repairApi';

const STATUS_COLORS = {
  pending: 'warning',
  diagnosing: 'info',
  waiting_for_parts: 'error',
  repaired: 'success',
  delivered: 'primary',
  cancelled: 'default',
};

export default function RepairTicketList() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading } = useGetRepairTicketsQuery({
    status: statusFilter,
    search: searchTerm,
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Repair Tickets
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/repairs/new')}
        >
          New Repair
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1 }}
            placeholder="Ticket No, IMEI, Model..."
          />
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="diagnosing">Diagnosing</MenuItem>
            <MenuItem value="waiting_for_parts">Waiting for Parts</MenuItem>
            <MenuItem value="repaired">Repaired</MenuItem>
            <MenuItem value="delivered">Delivered</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ticket No</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Device Model</TableCell>
              <TableCell>IMEI / Serial</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : data?.tickets?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No repair tickets found
                </TableCell>
              </TableRow>
            ) : (
              data?.tickets?.map((ticket) => (
                <TableRow key={ticket.id} hover>
                  <TableCell>{ticket.ticketNumber}</TableCell>
                  <TableCell>{new Date(ticket.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>{ticket.customer?.name}</TableCell>
                  <TableCell>{ticket.deviceModel}</TableCell>
                  <TableCell>{ticket.deviceImei || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={ticket.status.replace(/_/g, ' ').toUpperCase()}
                      color={STATUS_COLORS[ticket.status] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      onClick={() => navigate(`/repairs/${ticket.id}`)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
